import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult, AScore } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

// Tug of war: rapid-tap PULL to drag the rope marker to your side before the CPU.
export default function RassaKashi() {
  const [pos, setPos] = useState(0);      // -100 (cpu wins) .. +100 (you win)
  const [phase, setPhase] = useState("ready"); // ready | play | over
  const [win, setWin] = useState(false);
  const [round, setRound] = useState(1);
  const [pulling, setPulling] = useState(null); // 'you' | 'cpu' | null (drives lean)
  const posRef = useRef(0);
  const pt = useRef(null);

  const flash = (who) => { setPulling(who); clearTimeout(pt.current); pt.current = setTimeout(() => setPulling(null), 150); };

  const start = () => { setPhase("play"); setPos(0); posRef.current = 0; };

  const pull = () => {
    if (phase !== "play") return;
    posRef.current = Math.min(100, posRef.current + 5.5);
    setPos(posRef.current);
    flash("you");
    sfx.click?.();
    if (posRef.current >= 100) end(true);
  };

  const end = (w) => { setWin(w); setPhase("over"); if (w) sfx.win?.(); };

  const next = () => { setRound((r) => (win ? r + 1 : r)); setPhase("ready"); };

  // CPU pulls on its own while playing (own effect so its interval isn't cleared by the keydown effect)
  useEffect(() => {
    if (phase !== "play") return undefined;
    const cpuPow = 2.6 + round * 0.5;
    const id = setInterval(() => {
      posRef.current = Math.max(-100, posRef.current - (Math.random() * cpuPow + 0.4));
      setPos(posRef.current);
      flash("cpu");
      if (posRef.current <= -100) { clearInterval(id); setWin(false); setPhase("over"); }
    }, 90);
    return () => clearInterval(id);
  }, [phase, round]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); pull(); } };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); clearTimeout(pt.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  return (
    <div className="w-full text-center">
      <div className="flex justify-center gap-2">
        <AScore label="Round" value={round} />
        <AScore label="Pull" value={`${Math.round(pos)}`} />
      </div>
      <p className="my-3 text-sm text-kirana-brown/70">Rapidly tap PULL (or press Space) to drag the flag to <b>your</b> side!</p>

      <div data-testid="rk-scene" className="relative mx-auto mb-3 h-36 w-full max-w-md overflow-hidden rounded-md border-2 border-kirana-border bg-gradient-to-b from-[#d9efc4] to-[#8FBF6B]">
        <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-white/70" />
        <span className="absolute left-2 top-1 text-[10px] font-bold text-kirana-red">CPU</span>
        <span className="absolute right-2 top-1 text-[10px] font-bold text-kirana-green">YOU</span>
        {/* win-line danger zones */}
        <div className="absolute left-0 top-0 h-full w-[12%] bg-kirana-red/20" />
        <div className="absolute right-0 top-0 h-full w-[12%] bg-kirana-green/20" />

        {/* both teams + rope + knot slide together as the rope is dragged */}
        <div
          className="absolute top-1/2 flex items-center"
          style={{ left: `${50 + pos * 0.42}%`, transform: "translate(-50%,-50%)", transition: "left 75ms linear" }}
        >
          {/* CPU team leaning left */}
          <div
            className="origin-right whitespace-nowrap text-2xl leading-none"
            style={{ transform: `rotate(${pulling === "cpu" ? -16 : -6}deg)`, transition: "transform 100ms" }}
          >🧑🧒</div>
          <div className="h-1.5 w-14 bg-[#7a5230]" />
          {/* knot / flag */}
          <div data-testid="rk-flag" className="flex flex-col items-center">
            <div className="h-3 w-4 bg-kirana-red" />
            <div className="h-6 w-1 bg-[#3a2617]" />
          </div>
          <div className="h-1.5 w-14 bg-[#7a5230]" />
          {/* YOU team leaning right */}
          <div
            className="origin-left whitespace-nowrap text-2xl leading-none"
            style={{ transform: `rotate(${pulling === "you" ? 16 : 6}deg)`, transition: "transform 100ms" }}
          >🧒🧑</div>
        </div>
      </div>

      <div className="mt-3">
        {phase === "ready" && <ABtn data-testid="rk-start" onClick={start} tone="gold" className="px-8 py-3">Start Pulling</ABtn>}
        {phase === "play" && <ABtn data-testid="rk-pull" onClick={pull} tone="red" className="px-14 py-4 text-lg">PULL! 💪</ABtn>}
        {phase === "over" && (
          <>
            <AResult testId="rk-result" win={win} text={win ? "Zor lagaake… HEISHA! You won! 🎉" : "Oye, kheech gaye! CPU won. 😩"} />
            <div className="mt-3 flex justify-center"><ABtn data-testid="rk-again" onClick={next} tone="green">{win ? "Next Round" : "Try Again"}</ABtn></div>
          </>
        )}
      </div>
    </div>
  );
}
